import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import API, { apis } from "common/apis";
import { IApiError } from "common/interface/IApiError";
import { RootState } from "store/store";

type PidgeStatusResponse = {
  paramKey: string;
  isEnabled: boolean;
};

type PidgeStoreSlotConfigResponse = {
  paramKey: string;
  storeSlotConfig: Record<string, number[]>;
};

export interface IUpdatePidgeStoreSlotConfigPayload {
  storeId: number;
  slotIds: number[];
}

interface PidgeState {
  status: "idle" | "loading" | "saving" | "success" | "error";
  isEnabled: boolean;
  error?: string;
  slotConfigStatus: "idle" | "loading" | "saving" | "success" | "error";
  slotConfigError?: string;
  pidgeSlots: number[];
  storeSlotConfig?: Record<string, number[]>;
}

const initialState: PidgeState = {
  status: "idle",
  isEnabled: false,
  slotConfigStatus: "idle",
  pidgeSlots: [],
};

export const getPidgeStatus = createAsyncThunk<PidgeStatusResponse>(
  "pidge/status/get",
  async (_, thunkApi) => {
    try {
      const response = await API.get<PidgeStatusResponse>(apis.getPidgeEnabledStatus);
      return response.data;
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || "Failed to load Pidge status";
      return thunkApi.rejectWithValue({ message: errorMessage });
    }
  }
);

export const updatePidgeStatus = createAsyncThunk<boolean, boolean>(
  "pidge/status/update",
  async (enabled, thunkApi) => {
    try {
      await API.put(`${apis.togglePidgeEnabledStatus}?enabled=${enabled}`);
      return enabled;
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || "Failed to update Pidge status";
      return thunkApi.rejectWithValue({ message: errorMessage });
    }
  }
);

export const getPidgeSlots = createAsyncThunk<number[]>(
  "pidge/slots/get",
  async (_, thunkApi) => {
    try {
      const response = await API.get<number[]>(apis.getPidgeSlots);
      return response.data ?? [];
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || "Failed to load Pidge slots";
      return thunkApi.rejectWithValue({ message: errorMessage });
    }
  }
);

export const getPidgeStoreSlotConfig = createAsyncThunk<PidgeStoreSlotConfigResponse>(
  "pidge/storeSlotConfig/get",
  async (_, thunkApi) => {
    try {
      const response = await API.get<PidgeStoreSlotConfigResponse>(apis.pidgeStoreSlotConfig);
      return response.data;
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || "Failed to load Pidge store slot config";
      return thunkApi.rejectWithValue({ message: errorMessage });
    }
  }
);

export const updatePidgeStoreSlotConfig = createAsyncThunk<
  IUpdatePidgeStoreSlotConfigPayload,
  IUpdatePidgeStoreSlotConfigPayload
>(
  "pidge/storeSlotConfig/update",
  async (payload, thunkApi) => {
    try {
      await API.put(apis.pidgeStoreSlotConfig, payload);
      return payload;
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || "Failed to update Pidge store slot config";
      return thunkApi.rejectWithValue({ message: errorMessage });
    }
  }
);

const pidgeSlice = createSlice({
  name: "pidge",
  initialState,
  reducers: {
    removePidgeSlotConfig: (state) => {
      state.slotConfigStatus = "idle";
      state.slotConfigError = "";
      state.storeSlotConfig = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getPidgeStatus.pending, (state) => {
        state.status = "loading";
        state.error = "";
      })
      .addCase(getPidgeStatus.fulfilled, (state, action) => {
        state.status = "success";
        state.error = "";
        state.isEnabled = Boolean(action.payload?.isEnabled);
      })
      .addCase(getPidgeStatus.rejected, (state, action) => {
        const error = action.payload as IApiError;
        state.status = "error";
        if (error?.message) {
          state.error = error.message;
        }
      })
      .addCase(updatePidgeStatus.pending, (state) => {
        state.status = "saving";
        state.error = "";
      })
      .addCase(updatePidgeStatus.fulfilled, (state, action) => {
        state.status = "success";
        state.error = "";
        state.isEnabled = action.payload;
      })
      .addCase(updatePidgeStatus.rejected, (state, action) => {
        const error = action.payload as IApiError;
        state.status = "error";
        if (error?.message) {
          state.error = error.message;
        }
      })
      .addCase(getPidgeSlots.pending, (state) => {
        state.slotConfigStatus = "loading";
        state.slotConfigError = "";
      })
      .addCase(getPidgeSlots.fulfilled, (state, action) => {
        state.slotConfigStatus = "success";
        state.pidgeSlots = action.payload;
      })
      .addCase(getPidgeSlots.rejected, (state, action) => {
        const error = action.payload as IApiError;
        state.slotConfigStatus = "error";
        state.slotConfigError = error?.message;
      })
      .addCase(getPidgeStoreSlotConfig.pending, (state) => {
        state.slotConfigStatus = "loading";
        state.slotConfigError = "";
      })
      .addCase(getPidgeStoreSlotConfig.fulfilled, (state, action) => {
        state.slotConfigStatus = "success";
        state.storeSlotConfig = action.payload?.storeSlotConfig ?? {};
      })
      .addCase(getPidgeStoreSlotConfig.rejected, (state, action) => {
        const error = action.payload as IApiError;
        state.slotConfigStatus = "error";
        state.slotConfigError = error?.message;
      })
      .addCase(updatePidgeStoreSlotConfig.pending, (state) => {
        state.slotConfigStatus = "saving";
        state.slotConfigError = "";
      })
      .addCase(updatePidgeStoreSlotConfig.fulfilled, (state, action) => {
        state.slotConfigStatus = "success";
        if (state.storeSlotConfig) {
          state.storeSlotConfig[String(action.payload.storeId)] = action.payload.slotIds;
        }
      })
      .addCase(updatePidgeStoreSlotConfig.rejected, (state, action) => {
        const error = action.payload as IApiError;
        state.slotConfigStatus = "error";
        state.slotConfigError = error?.message;
      });
  },
});

export const { removePidgeSlotConfig } = pidgeSlice.actions;
export const selectPidgeState = (state: RootState) => state.pidge;
export default pidgeSlice.reducer;
